import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { obtenerCantidadGanadoEnCorral } from '../../api-client/ganado/ganado.api';
import AppDialog from '../../components/ui/AppDialog';
import ComboBox from '../../components/ui/Combobox';
import { useCorralesStore, type AnimalId } from '../../stores/corrales/corrales.store';
import { useUIStore } from '../../stores/ui/ui.store';
import transformToOptions, { type ComboBoxDataItem } from '../../utils/transform-to-options';

interface TraspasoMasivoDialogoProps {
  open: boolean;
  onClose: () => void;
  animales: AnimalId[];
  idCorralOrigen: number;
}

const TraspasoMasivoDialogo = ({
  open,
  onClose,
  animales,
  idCorralOrigen
}: TraspasoMasivoDialogoProps) => {
  const { loading } = useUIStore();
  const { actives, traspasarMasivo, cargarActivos } = useCorralesStore();
  const [corralesDestino, setCorralesDestino] = useState<ComboBoxDataItem[]>([]);
  const [showConfirmAccion, setShowConfirmAccion] = useState(false);
  const [showConformCierre, setShowConfirmCierre] = useState(false);
  const form = useFormik({
    initialValues: {
      fechaTraspaso: dayjs(new Date()),
      idCorralDestino: 0
    },
    validationSchema: yup.object({
      fechaTraspaso: yup.date().required('La fecha de traspaso es requerida'),
      idCorralDestino: yup
        .number()
        .required('El corral destino es requerido')
        .min(1, 'El corral destino es requerido')
    }),
    onSubmit: async (values) => {
      await traspasarMasivo({
        idCorralOrigen: idCorralOrigen,
        idCorralDestino: values.idCorralDestino,
        fechaTraspaso: values.fechaTraspaso.toDate(),
        animales: animales
      });

      onClose();
    }
  });

  useEffect(() => {
    cargarActivos();
  }, []);

  useEffect(() => {
    setCorralesDestino(transformToOptions(actives.filter((c) => c.id !== idCorralOrigen)));
  }, [actives, idCorralOrigen]);

  const confirmAccion = async () => {
    setShowConfirmAccion(false);
    await form.setTouched({
      fechaTraspaso: true,
      idCorralDestino: true
    });
    const errors = await form.validateForm();
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) return;

    const response = await obtenerCantidadGanadoEnCorral(idCorralOrigen);
    if (response.result <= animales.length) {
      setShowConfirmCierre(true);
      return;
    }

    await form.submitForm();
  };

  const confirmCierre = async () => {
    setShowConfirmCierre(false);
    await form.submitForm();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>TRASPASO MASIVO</DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={form.submitForm}>
            <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
              <Grid size={6}>
                <Typography>Fecha traspaso</Typography>
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label=""
                  value={form.values.fechaTraspaso}
                  onChange={(date) => form.setFieldValue('fechaTraspaso', date)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={6}>
                <Typography>Corral destino</Typography>
              </Grid>
              <Grid size={6}>
                <ComboBox
                  name="idCorralDestino"
                  label=""
                  options={corralesDestino}
                  value={form.values.idCorralDestino}
                  disabled={loading}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.idCorralDestino && Boolean(form.errors.idCorralDestino)}
                  helperText={
                    form.touched.idCorralDestino ? form.errors.idCorralDestino : undefined
                  }
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={() => setShowConfirmAccion(true)}
            disabled={loading}
            startIcon={<CheckCircleIcon />}
            disableElevation
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            startIcon={<ExitToAppIcon />}
            onClick={onClose}
            disabled={loading}
            disableElevation
          >
            Salir
          </Button>
        </DialogActions>
      </Dialog>
      <AppDialog
        open={showConfirmAccion}
        onClose={() => setShowConfirmAccion(false)}
        title="Confirme"
        message="¿Está seguro de guardar y registrar el movimiento?"
        buttons={
          <>
            <Button color="secondary" variant="contained" disableElevation onClick={confirmAccion}>
              Sí
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setShowConfirmAccion(false)}
            >
              No
            </Button>
          </>
        }
      />
      <AppDialog
        open={showConformCierre}
        onClose={() => setShowConfirmCierre(false)}
        title="Confirme"
        message="El traspaso dejará el corral vacío y se dará por cerrado. ¿Desea continuar con su traspaso?"
        buttons={
          <>
            <Button color="secondary" variant="contained" disableElevation onClick={confirmCierre}>
              Sí
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setShowConfirmCierre(false)}
            >
              No
            </Button>
          </>
        }
      />
    </>
  );
};

export default TraspasoMasivoDialogo;
